import { CloseOutlined } from '@ant-design/icons';
import { useVoyagerCRUDActions } from '@mm/state/actions/VoyagerCRUDActions';
import { SearchAttributes } from '@mm/state/interfaces/leads';
import { MyLeadsAttributes } from '@mm/state/interfaces/myLeads';
import {
  VoyagerKeyType,
  VoyagerLocationsSearch,
  VoyagerQueryType,
  VoyagerRemoteAttributeSearch,
  VOYAGER_FRIENDLY_NAMES,
  VOYAGER_KEYS,
} from '@mm/state/interfaces/voyagerLeads';
import { selectVoyagerFilterAttributes } from '@mm/state/selectors/voyagerLeadsSelectors';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RemoteSearchInput } from '../../../../../../../common/components/RemoteSearchInput/RemoteSearchInput';
import styles from './VoyagerRemoteFiltersSearch.module.css';

type Props = {
  query?: VoyagerQueryType[] | SearchAttributes;
  attribute?: VoyagerKeyType;
  placeholder?: string;
  searchDisabled?: boolean;
  onSelect?: (
    key: VoyagerKeyType,
    value: string,
    operator?: 'in' | 'not_in',
  ) => void;
  isLocation?: boolean;
  exclude?: boolean;
  useStateResults?: boolean;
  lengthLimit?: number;
  className?: string;
};

type SuggestedResult = {
  key: string;
  name: string;
};

export const LOCATION_KEYS = [
  VOYAGER_KEYS.state,
  VOYAGER_KEYS.city,
  VOYAGER_KEYS.zip,
];

export const VoyagerRemoteFiltersSearch = ({
  query,
  attribute,
  placeholder,
  searchDisabled,
  onSelect,
  isLocation,
  exclude,
  useStateResults,
  lengthLimit,
  className,
}: Props) => {
  const minlength = lengthLimit || 3;
  const attributes = useSelector(selectVoyagerFilterAttributes);

  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState<SuggestedResult[]>([]);
  const { retrieveRemoteAttributes, retrieveLocationAttributes } =
    useVoyagerCRUDActions();

  const mapResults = (attribute: VoyagerKeyType, value: string) => ({
    key: `${attribute}/${uniqueId()}`,
    name: value,
  });

  const selectedItems: SuggestedResult[] = !query
    ? []
    : Array.isArray(query)
    ? query?.flatMap(item =>
        (item.field === attribute ||
          (isLocation && LOCATION_KEYS.includes(item.field as any))) &&
        (exclude ? item.operator === 'not_in' : !item.operator)
          ? item.values.map(val => ({
              key: `${item.field}/${uniqueId()}`,
              name: val,
            }))
          : [],
      )
    : attribute
    ? query?.[attribute]?.map((val: string) => ({
        key: `${attribute}/${uniqueId()}`,
        name: val,
      }))
    : [];

  const onFetch = async query => {
    const trimmedQuery = query.trim().toLowerCase();
    setSearchValue(trimmedQuery);
    if (trimmedQuery) {
      if (attribute && !useStateResults) {
        const results: VoyagerRemoteAttributeSearch =
          await retrieveRemoteAttributes(attribute, trimmedQuery);
        setResults(
          results.items.map(value => mapResults(results.attribute, value)),
        );
      }
      if (attribute && useStateResults) {
        const results = attributes
          .find(attr => attr.attribute === attribute)
          ?.items?.map(item => mapResults(attribute, item))
          .filter(i => i.name.toLocaleLowerCase().includes(trimmedQuery));
        if (results) {
          setResults(results);
        }
      }
      if (isLocation) {
        const results: VoyagerLocationsSearch[] =
          await retrieveLocationAttributes(trimmedQuery);
        setResults(results.map(item => mapResults(item.attribute, item.value)));
      }
    }
  };

  const clear = () => {
    setSearchValue('');
    setResults([]);
  };

  const friendlyKeyName = (key: string) =>
    VOYAGER_FRIENDLY_NAMES?.[key?.split('/')?.[0]];

  const onSelectItem = (selectedItem: SuggestedResult) => {
    const isExisting = selectedItems?.find(
      item => item.name === selectedItem.name,
    );
    if (isExisting) return;
    onSelect?.(
      selectedItem.key.split('/')?.[0] as VoyagerKeyType,
      selectedItem.name,
      exclude ? 'not_in' : undefined,
    );
    clear();
  };

  const onDeleteItem = (item: SuggestedResult) => {
    onSelect?.(item.key.split('/')?.[0] as VoyagerKeyType, item.name);
  };

  const renderSuggestion = (suggestion: SuggestedResult) => {
    const suggestKey = friendlyKeyName(suggestion.key) || suggestion.key;

    return (
      <div className={styles.suggestionContainer} key={suggestion.key}>
        <div className={styles.suggestion}>{suggestion.name}</div>
        {isLocation && suggestKey && (
          <div className={styles.key}>{suggestKey}</div>
        )}
      </div>
    );
  };

  return (
    <div className={classNames(styles.container, className)}>
      <RemoteSearchInput
        value={searchValue}
        placeholder={`${placeholder} (min ${minlength} chars)`}
        lengthLimit={minlength}
        disabled={searchDisabled}
        onFetch={onFetch}
        results={results}
        onSelectedItem={onSelectItem}
        renderSuggestion={renderSuggestion}
        className={styles.input}
        style={{ border: '1px solid var(--color-gray-5)', borderRadius: '4px' }}
        withSearchIcon
        clearInputOnSelect
      />
      {!!selectedItems?.length && (
        <div className={styles.results}>
          {selectedItems?.map(selectedItem => (
            <div className={styles.locationItemWrapper} key={selectedItem.key}>
              <span className={styles.key}>
                {friendlyKeyName(selectedItem.key)}:
              </span>
              {selectedItem.name}
              <CloseOutlined
                className={styles.close}
                onClick={() => onDeleteItem(selectedItem)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
